

import React from 'react';
import { Quest, QuestCategory } from '../types';
import { CreatorIcon, ExplorerIcon, SkillBuilderIcon, TeamUpIcon, CheckIcon, RejectIcon, QuestlineIcon } from './icons';

interface QuestCardProps {
  quest: Quest;
  variant: 'offer' | 'active';

  onChoose?: (quest: Quest) => void;
  onReject?: (quest: Quest) => void;
  onComplete?: (quest: Quest) => void;
  onShowDetails: (quest: Quest) => void;
  isCompleted?: boolean;
  isChoiceDisabled?: boolean;
  disabledReason?: string;
  completedQuestIds: number[];
  
}

const categoryConfig = {
  [QuestCategory.Creator]: {
    Icon: CreatorIcon,
    gradientFrom: 'from-blue-500',
    gradientTo: 'to-sky-500',
    borderColor: 'border-blue-500',
    ringColor: 'ring-blue-400',
  },
  [QuestCategory.Explorer]: {
    Icon: ExplorerIcon,
    gradientFrom: 'from-green-500',
    gradientTo: 'to-emerald-500',
    borderColor: 'border-green-500',
    ringColor: 'ring-green-400',
  },
  [QuestCategory.SkillBuilder]: {
    Icon: SkillBuilderIcon,
    gradientFrom: 'from-yellow-500',
    gradientTo: 'to-amber-500',
    borderColor: 'border-yellow-500',
    ringColor: 'ring-yellow-400',
  },
  [QuestCategory.TeamUp]: {
    Icon: TeamUpIcon,
    gradientFrom: 'from-red-500',
    gradientTo: 'to-rose-500',
    borderColor: 'border-red-500',
    ringColor: 'ring-red-400',
  },
};

const IconButton: React.FC<{ onClick: () => void, children: React.ReactNode, className?: string, 'aria-label': string, title?: string }> = 
  ({ onClick, children, className, ...props }) => (
  <button
    onClick={onClick}
    className={`p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-600/50 focus:outline-none focus:ring-2 focus:ring-black/50 dark:focus:ring-white/50 transition-colors ${className}`}
    {...props}
  >
    {children}
  </button>
);


const QuestCard: React.FC<QuestCardProps> = ({ quest, variant, onChoose, onReject, onComplete, onShowDetails, isCompleted, isChoiceDisabled, disabledReason, completedQuestIds }) => {
  const config = categoryConfig[quest.category];

  const isSkillBuilderRepeat = quest.category === QuestCategory.SkillBuilder && completedQuestIds.includes(quest.id);

  const descriptionToShow = (isSkillBuilderRepeat && quest.repeatDescription) 
    ? quest.repeatDescription 
    : quest.description;

  return (
    <div className={`relative bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border-t-4 ${config.borderColor} flex flex-col h-full`}>
      <div className="flex items-start space-x-4">
        <div className={`p-3 rounded-full bg-gradient-to-br ${config.gradientFrom} ${config.gradientTo}`}>
          <config.Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <p className={`font-bold text-sm uppercase tracking-wider bg-clip-text text-transparent bg-gradient-to-r ${config.gradientFrom} ${config.gradientTo}`}>
            {quest.category} Quest
          </p>
          <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-1">{quest.title}</h3>
          {quest.prerequisiteQuestId && (
            <div className="flex items-center gap-1.5 text-orange-500 dark:text-orange-400 mt-2">
              <QuestlineIcon className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wider">Questline Step</span>
            </div>
          )}
          <p className="text-slate-600 dark:text-slate-400 mt-2">{descriptionToShow}</p>
          <button 
            onClick={() => onShowDetails(quest)} 
            className="text-sm font-semibold mt-3 text-left text-[var(--primary-600)] hover:text-[var(--primary-700)] dark:text-[var(--primary-400)] dark:hover:text-[var(--primary-500)]"
          >
            More Details...
          </button>
        </div>
      </div>
      
      <div className="mt-auto pt-4 border-t border-slate-200 dark:border-slate-700/50 flex items-end">
        {variant === 'offer' && onChoose && onReject && (
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center space-x-2">
              <IconButton 
                onClick={() => onReject(quest)} 
                aria-label="Reject Quest"
                title="Permanently remove this quest. This can't be undone."
              >
                <RejectIcon className="w-6 h-6 text-red-400" />
              </IconButton>
            </div>
            <button
              onClick={() => onChoose(quest)}
              disabled={isChoiceDisabled}
              title={isChoiceDisabled ? disabledReason : 'Choose this quest'}
              className="px-4 py-2 text-white font-bold rounded-lg shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-75 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'var(--primary-600)','--tw-ring-color': 'var(--primary-500)' } as React.CSSProperties}
              onMouseOver={e => { if(!isChoiceDisabled) e.currentTarget.style.backgroundColor = 'var(--primary-700)'}}
              onMouseOut={e => { if(!isChoiceDisabled) e.currentTarget.style.backgroundColor = 'var(--primary-600)'}}
            >
              <CheckIcon className="w-5 h-5" />
              <span>Choose</span>
            </button>
          </div>
        )}
        {variant === 'active' && onComplete && (
           <button
            onClick={() => onComplete(quest)}
            disabled={isCompleted}
            className={`w-full px-6 py-3 font-bold rounded-lg shadow-md transition-all duration-200 focus:outline-none focus:ring-2 ${config.ringColor} focus:ring-opacity-75
              ${isCompleted
                ? 'bg-slate-200 dark:bg-slate-700 text-slate-500 cursor-not-allowed'
                : `bg-gradient-to-r ${config.gradientFrom} ${config.gradientTo} text-white hover:opacity-90`
              }`}
          >
            {isCompleted ? 'Quest Complete!' : 'Complete Quest'}
          </button>
        )}
      </div>
    </div>
  );
};

export default QuestCard;